// src/components/SalesDashboard/SalesOrderList.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Eye, RefreshCw, Search, ChevronLeft, ChevronRight, FileText,
} from 'lucide-react';
import api from '../api';

const S = `
  .sol-wrap { font-family: 'Inter', sans-serif; color: #000; }
  .sol-topbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
  .sol-topbar h2 { font-size: 22px; font-weight: 800; color: #1e293b; margin: 0; }

  .sol-tabs { display: flex; gap: 6px; margin-bottom: 20px; flex-wrap: wrap; }
  .sol-tab {
    padding: 7px 18px; border-radius: 20px; font-size: 12px; font-weight: 700;
    cursor: pointer; border: 2px solid #e2e8f0; background: white; color: #1a1a1a;
    transition: all .15s;
  }
  .sol-tab.active { background: #1e1b4b; border-color: #1e1b4b; color: white; }
  .sol-tab:hover:not(.active) { border-color: #6366f1; color: #6366f1; }

  .sol-controls { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; align-items: center; }
  .sol-search {
    display: flex; align-items: center; gap: 8px; background: white;
    border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 14px; flex: 1; min-width: 200px;
  }
  .sol-search input { border: none; outline: none; font-size: 13px; width: 100%; color: #1e293b; background: transparent; }

  .sol-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; }
  .sol-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .sol-table thead tr { background: #f8fafc; }
  .sol-table th { padding: 11px 14px; text-align: left; font-size: 11px; font-weight: 700; color: #1a1a1a; text-transform: uppercase; letter-spacing: .4px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
  .sol-table td { padding: 13px 14px; border-bottom: 1px solid #f1f5f9; color: #1e293b; vertical-align: middle; }
  .sol-table tbody tr:last-child td { border-bottom: none; }
  .sol-table tbody tr:hover td { background: #f8fafc; }

  .sol-btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px;
    border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer;
    border: none; transition: opacity .15s; white-space: nowrap;
  }
  .sol-btn:hover { opacity: .88; }
  .sol-btn-primary { background: linear-gradient(135deg,#1e1b4b,#312e81); color: white; }
  .sol-btn-outline { background: white; color: #1a1a1a; border: 1.5px solid #e2e8f0; }

  .sol-action-btn {
    display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px;
    border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
    border: 1.5px solid; transition: opacity .15s;
  }
  .sol-action-btn:hover { opacity: .8; }
  .sol-action-btn:disabled { opacity: .5; cursor: not-allowed; }

  .sol-badge { display: inline-block; padding: 3px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; white-space: nowrap; }

  .sol-pagination { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 8px; }
  .sol-page-btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; border: 1.5px solid #e2e8f0; background: white; color: #1a1a1a; cursor: pointer; }
  .sol-page-btn:disabled { opacity: .4; cursor: not-allowed; }

  .sol-empty { text-align: center; padding: 60px 24px; color: #1a1a1a; }
  .sol-empty-icon { font-size: 48px; margin-bottom: 12px; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const STATUS_CONFIG = {
  CONFIRMED:     { bg: '#dbeafe', color: '#1d4ed8', label: 'Confirmed' },
  SENT_TO_PRODUCTION: { bg: '#ede9fe', color: '#6d28d9', label: 'Sent to Production' },
  IN_PRODUCTION: { bg: '#fef3c7', color: '#b45309', label: 'In Production' },
  COMPLETED:     { bg: '#d1fae5', color: '#065f46', label: 'Completed' },
  INVOICED:      { bg: '#ede9fe', color: '#6d28d9', label: 'Invoiced' },
  CANCELLED:     { bg: '#fee2e2', color: '#b91c1c', label: 'Cancelled' },
};

const PRIORITY_CONFIG = {
  1: { bg: '#fee2e2', color: '#b91c1c', label: 'URGENT' },
  2: { bg: '#fef3c7', color: '#b45309', label: 'HIGH' },
  3: { bg: '#dbeafe', color: '#1d4ed8', label: 'NORMAL' },
  4: { bg: '#ecfccb', color: '#3f6212', label: 'LOW' },
  5: { bg: '#f1f5f9', color: '#475569', label: 'VERY LOW' },
};

const TABS = [
  { key: '', label: 'All' },
  { key: 'CONFIRMED', label: 'Confirmed' },
  { key: 'SENT_TO_PRODUCTION', label: 'Sent to Production' },
  { key: 'IN_PRODUCTION', label: 'In Production' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'INVOICED', label: 'Invoiced' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { bg: '#f1f5f9', color: '#64748b', label: status };
  return <span className="sol-badge" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
};

const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG[3];
  return <span className="sol-badge" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
};

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
    <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
  </div>
);

export default function SalesOrderList({ onViewDetail, onCreateNew }) {
  const [tab, setTab] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const perPage = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (tab) params.status = tab;
      const res = await api.get('/sales/orders', { params });
      let rows = res.data.data || [];
      if (search.trim()) {
        const q = search.toLowerCase();
        rows = rows.filter(o =>
          o.order_code?.toLowerCase().includes(q) ||
          o.customer_name?.toLowerCase().includes(q)
        );
      }
      setData(rows);
      setTotal(res.data.total || 0);
    } catch {
      setData([]);
    }
    setLoading(false);
  }, [tab, page, search]);

  useEffect(() => { setPage(1); }, [tab]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / perPage);

  return (
    <>
      <style>{S}</style>
      <div className="sol-wrap">
        {/* Header */}
        <div className="sol-topbar">
          <div>
            <h2>Sales Orders</h2>
            <div style={{ fontSize: 13, color: '#1a1a1a', marginTop: 2 }}>
              {total} order{total !== 1 ? 's' : ''} total
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="sol-btn sol-btn-outline" onClick={load}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="sol-btn sol-btn-primary" onClick={onCreateNew}>
              <Plus size={14} /> New Direct Order
            </button>
          </div>
        </div>

        {/* Status tabs */}
        <div className="sol-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`sol-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="sol-controls">
          <div className="sol-search">
            <Search size={14} color="#94a3b8" />
            <input
              placeholder="Search order code or customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="sol-card">
          {loading ? <Spinner /> : data.length === 0 ? (
            <div className="sol-empty">
              <div className="sol-empty-icon">📦</div>
              <div style={{ fontWeight: 700, color: '#1a1a1a', fontSize: 16 }}>No orders found</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Create a direct order or convert an approved quotation</div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="sol-table">
                  <thead>
                    <tr>
                      <th>Order Code</th>
                      <th>Customer</th>
                      <th>Priority</th>
                      <th>Items</th>
                      <th>Qty</th>
                      <th>Total (₹)</th>
                      <th>Advance (₹)</th>
                      <th>Balance (₹)</th>
                      <th>Delivery</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(o => {
                      const deliveryDate = o.expected_delivery && o.expected_delivery !== 'None'
                        ? new Date(o.expected_delivery)
                        : null;
                      const daysLeft = deliveryDate
                        ? Math.ceil((deliveryDate - Date.now()) / (1000 * 60 * 60 * 24))
                        : null;

                      return (
                        <tr key={o.id}>
                          <td>
                            <span style={{ fontWeight: 700, color: '#1e1b4b', fontFamily: 'monospace', fontSize: 13 }}>
                              {o.order_code}
                            </span>
                            {o.quotation_id && (
                              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                From quotation
                              </div>
                            )}
                          </td>
                          <td style={{ fontWeight: 600 }}>{o.customer_name}</td>
                          <td><PriorityBadge priority={o.priority} /></td>
                          <td style={{ textAlign: 'center' }}>{o.total_items}</td>
                          <td style={{ textAlign: 'center' }}>{o.total_qty}</td>
                          <td style={{ fontWeight: 700 }}>₹{fmt(o.total_amount)}</td>
                          <td style={{ color: '#10b981', fontWeight: 600 }}>₹{fmt(o.advance_received)}</td>
                          <td style={{ color: o.balance_due > 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                            ₹{fmt(o.balance_due)}
                          </td>
                          <td>
                            {deliveryDate ? (
                              <div>
                                <div style={{ fontSize: 12 }}>{deliveryDate.toLocaleDateString()}</div>
                                {daysLeft !== null && (
                                  <div style={{
                                    fontSize: 10, fontWeight: 600,
                                    color: daysLeft < 0 ? '#dc2626' : daysLeft <= 3 ? '#f59e0b' : '#64748b',
                                  }}>
                                    {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                                  </div>
                                )}
                              </div>
                            ) : <span style={{ color: '#94a3b8' }}>—</span>}
                          </td>
                          <td><StatusBadge status={o.status} /></td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <button
                                className="sol-action-btn"
                                style={{ background: '#eef2ff', color: '#6366f1', borderColor: '#c7d2fe' }}
                                onClick={() => onViewDetail(o.id)}
                              >
                                <Eye size={12} /> View
                              </button>
                              {o.status === 'COMPLETED' && (
                                <button
                                  className="sol-action-btn"
                                  style={{ background: '#f0fdf4', color: '#15803d', borderColor: '#86efac' }}
                                  onClick={() => onViewDetail(o.id)}
                                  title="Generate Invoice"
                                >
                                  <FileText size={12} /> Invoice
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="sol-pagination">
                  <span style={{ fontSize: 13, color: '#1a1a1a' }}>
                    Page {page} of {totalPages} · {total} orders
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="sol-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <button className="sol-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
